import { motion } from "framer-motion";
import { Gift, ShieldX, HandshakeIcon } from "lucide-react";

const PROMISES = [
    {
        icon: Gift,
        title: "100% gratuit",
        desc: "Coway est entièrement gratuit pour les utilisateurs.",
    },
    {
        icon: ShieldX,
        title: "Aucune commission",
        desc: "Coway ne prélève aucun pourcentage sur vos trajets ou vos frais partagés.",
    },
    {
        icon: HandshakeIcon,
        title: "Organisation libre",
        desc: "Les utilisateurs restent libres d'organiser le partage des frais entre eux.",
    },
];

export default function Pledge() {
    return (
        <section
            id="pledge"
            data-testid="pledge-section"
            className="relative py-24 lg:py-32 overflow-hidden"
        >
            <div className="absolute inset-0 -z-10">
                <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_50%_50%,rgba(81,199,173,0.06),transparent_70%)]" />
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <p className="text-xs text-coway uppercase tracking-[0.3em] font-semibold mb-5">
                        Notre engagement
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
                        Une promesse,
                        <br />
                        <span className="text-slate-400">trois certitudes.</span>
                    </h2>
                    <p className="mt-6 text-base lg:text-lg text-slate-600">
                        Coway met en relation des voyageurs qui partagent leur
                        route — et leurs frais. L'objectif est simple : rendre
                        chaque trajet plus accessible, plus humain, plus malin.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
                    {PROMISES.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{
                                duration: 0.6,
                                delay: i * 0.1,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            whileHover={{ y: -6 }}
                            data-testid={`pledge-card-${i + 1}`}
                            className="relative p-8 lg:p-10 rounded-3xl bg-white border border-slate-200 hover:border-coway/40 transition-colors card-elevated group overflow-hidden text-center"
                        >
                            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-60 h-60 rounded-full bg-coway/[0.08] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                            <div className="relative z-10">
                                <motion.span
                                    initial={{ scale: 0.85 }}
                                    whileInView={{ scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 0.6,
                                        delay: i * 0.1 + 0.15,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="inline-flex w-14 h-14 rounded-2xl bg-coway-soft border border-coway/15 items-center justify-center mb-6"
                                >
                                    <p.icon
                                        className="w-6 h-6 text-coway"
                                        strokeWidth={1.7}
                                    />
                                </motion.span>
                                <h3 className="font-display text-2xl font-bold tracking-tight text-slate-900">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-base text-slate-500 leading-relaxed max-w-xs mx-auto">
                                    {p.desc}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="mt-12 flex items-center justify-center"
                >
                    <span
                        data-testid="pledge-badge"
                        className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-white border border-slate-200 shadow-sm"
                    >
                        <span className="relative flex w-2.5 h-2.5">
                            <span className="absolute inline-flex w-full h-full rounded-full bg-coway opacity-60 animate-ping" />
                            <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-coway" />
                        </span>
                        <span className="text-sm font-medium text-slate-700">
                            Édité par Coway, France — Plateforme de mise en
                            relation.
                        </span>
                    </span>
                </motion.div>
            </div>
        </section>
    );
}
